import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import ConfirmationModal from '../components/Helper/ConfirmationModal';
import ShareVaultModal from '../components/ShareVaultModal';
import ChangeVaultMasterPasswordModal from '../components/ChangeVaultMasterPasswordModal';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  getVaultFoldersByOwner,
  createVaultFolder,
  updateVaultFolder,
  deleteVaultFolder,
  createVault,
  updateVault,
  deleteVault,
  checkIfUserKnowsPassword,
} from '../store/slices/vaultFolderSlice';
import { getVaultMemberMe, getVaultMemberMePending, getVaultMembers, removeVaultMemberByUserName } from '../store/slices/vaultMemberSlice';
import type { VaultMemberItem } from '../store/slices/vaultMemberSlice';

export interface FolderItem {
  id: string;
  name: string;
}

export interface TableItem {
  id: string;
  name: string;
  folderId: string;
}

export type SelectedTableInfo = {
  tableId: string;
  tableName: string;
  folderId: string;
  folderName: string;
};

type NameModalMode = 'addFolder' | 'renameFolder' | 'addTable' | 'renameTable';

interface AddTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, password: string) => void;
  title: string;
  namePlaceholder: string;
  passwordLabel: string;
  passwordPlaceholder: string;
  cancelText: string;
  saveText: string;
}

const AddTableModal: React.FC<AddTableModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  namePlaceholder,
  passwordLabel,
  passwordPlaceholder,
  cancelText,
  saveText,
}) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  useEffect(() => {
    if (isOpen) {
      setName('');
      setPassword('');
    }
  }, [isOpen]);
  const handleSave = () => {
    const trimmedName = name.trim();
    if (trimmedName && password) {
      onSave(trimmedName, password);
      onClose();
    }
  };
  if (!isOpen) return null;
  const content = (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={onClose} />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/80">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {namePlaceholder}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={namePlaceholder}
                autoFocus
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {passwordLabel}
              </label>
              <input
                data-add-table-password
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                placeholder={passwordPlaceholder}
                autoComplete="off"
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow input-mask-password"
              />
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700/80 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors font-medium"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!name.trim() || !password}
              className="px-5 py-2.5 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg shadow-primary/25"
            >
              {saveText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
  return createPortal(content, document.body);
};

interface NameEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  title: string;
  initialValue: string;
  placeholder?: string;
  cancelText: string;
  saveText: string;
}

const NameEditModal: React.FC<NameEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  initialValue,
  placeholder = '',
  cancelText,
  saveText,
}) => {
  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    if (isOpen) setValue(initialValue);
  }, [isOpen, initialValue]);
  const handleSave = () => {
    const trimmed = value.trim();
    if (trimmed) onSave(trimmed);
    onClose();
  };
  if (!isOpen) return null;
  const content = (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={onClose} />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/80">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          </div>
          <div className="p-6">
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder={placeholder}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow"
              autoFocus
            />
          </div>
          <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700/80 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors font-medium"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!value.trim()}
              className="px-5 py-2.5 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg shadow-primary/25"
            >
              {saveText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
  return createPortal(content, document.body);
};

interface RenameWithPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, password: string) => void;
  title: string;
  initialName: string;
  namePlaceholder: string;
  passwordLabel: string;
  passwordPlaceholder: string;
  cancelText: string;
  saveText: string;
}

const RenameWithPasswordModal: React.FC<RenameWithPasswordModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  initialName,
  namePlaceholder,
  passwordLabel,
  passwordPlaceholder,
  cancelText,
  saveText,
}) => {
  const [name, setName] = useState(initialName);
  const [password, setPassword] = useState('');
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setPassword('');
    }
  }, [isOpen, initialName]);
  const handleSave = () => {
    const trimmedName = name.trim();
    if (trimmedName && password) {
      onSave(trimmedName, password);
      onClose();
    }
  };
  if (!isOpen) return null;
  const content = (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={onClose} />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/80">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {namePlaceholder}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={namePlaceholder}
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {passwordLabel}
              </label>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                placeholder={passwordPlaceholder}
                autoComplete="off"
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow input-mask-password"
              />
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700/80 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors font-medium"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!name.trim() || !password}
              className="px-5 py-2.5 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg shadow-primary/25"
            >
              {saveText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
  return createPortal(content, document.body);
};

const WORKSPACE_LAST_FOLDER_KEY_PREFIX = 'smartpm_workspace_lastFolder_';
const SHARED_FOLDER_ID = '__shared__';

const Workspace: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.auth.user?.id);

  const vaultFolderItems = useAppSelector((state) => state.vaultFolder.items);
  const vaultFolderListLoading = useAppSelector((state) => state.vaultFolder.operationLoading.list);
  const meVaults = useAppSelector((state) => state.vaultMember.meVaults);

  useEffect(() => {
    if (userId) {
      dispatch(getVaultFoldersByOwner(userId));
      dispatch(getVaultMemberMe());
      dispatch(getVaultMemberMePending());
    }
  }, [userId, dispatch]);

  const refreshWorkspaceData = useCallback(() => {
    if (userId) {
      dispatch(getVaultFoldersByOwner(userId));
      dispatch(getVaultMemberMe());
      dispatch(getVaultMemberMePending());
    }
  }, [userId, dispatch]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userId) {
        dispatch(getVaultFoldersByOwner(userId));
        dispatch(getVaultMemberMe());
        dispatch(getVaultMemberMePending());
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [userId, dispatch]);

  const folders: FolderItem[] = useMemo(
    () => [
      ...vaultFolderItems.map((item) => ({
        id: item.id,
        name: item.name ?? '',
      })),
      ...(meVaults.length > 0
        ? [{ id: SHARED_FOLDER_ID, name: t('pages.workspace.sharedFolder') }]
        : []),
    ],
    [vaultFolderItems, meVaults.length, t]
  );

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // Restore last opened folder from localStorage after folders are loaded
  useEffect(() => {
    if (!userId || vaultFolderListLoading || selectedFolderId !== null) return;
    const hasAnyFolder = vaultFolderItems.length > 0 || meVaults.length > 0;
    if (!hasAnyFolder) return;
    try {
      const key = `${WORKSPACE_LAST_FOLDER_KEY_PREFIX}${userId}`;
      const saved = localStorage.getItem(key);
      if (
        saved &&
        (vaultFolderItems.some((f) => f.id === saved) || (saved === SHARED_FOLDER_ID && meVaults.length > 0))
      ) {
        setSelectedFolderId(saved);
      }
    } catch {
      // ignore
    }
  }, [userId, vaultFolderListLoading, vaultFolderItems, meVaults.length, selectedFolderId]);

  // Clear selection when there are no folders, or selected folder no longer exists
  useEffect(() => {
    if (folders.length === 0 && selectedFolderId !== null) {
      setSelectedFolderId(null);
    } else if (selectedFolderId !== null && !folders.some((f) => f.id === selectedFolderId)) {
      setSelectedFolderId(null);
    }
  }, [folders.length, folders, selectedFolderId]);

  const selectedFolderItem = selectedFolderId
    ? vaultFolderItems.find((f) => f.id === selectedFolderId)
    : null;
  const selectedFolder = selectedFolderId
    ? folders.find((f) => f.id === selectedFolderId)
    : null;

  const isSharedFolderSelected = selectedFolderId === SHARED_FOLDER_ID;
  const tablesInFolder: TableItem[] = isSharedFolderSelected
    ? meVaults.map((v) => ({
        id: v.vaultId,
        name: v.vaultName ?? '',
        folderId: SHARED_FOLDER_ID,
      }))
    : (selectedFolderItem?.vaults ?? []).map((v) => ({
        id: v.id,
        name: v.name ?? '',
        folderId: v.folderId ?? selectedFolderId ?? '',
      }));

  const [nameModal, setNameModal] = useState<{
    mode: NameModalMode;
    folderId?: string;
    tableId?: string;
    currentName?: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'folder' | 'table';
    id: string;
    name: string;
  } | null>(null);
  const [shareVaultTarget, setShareVaultTarget] = useState<{ vaultId: string; vaultName: string } | null>(null);
  const [changePasswordVaultTarget, setChangePasswordVaultTarget] = useState<{
    vaultId: string;
    vaultName: string;
  } | null>(null);
  const [vaultInfoModal, setVaultInfoModal] = useState<{
    vaultId: string;
    vaultName: string;
    members?: VaultMemberItem[];
    error?: string;
  } | null>(null);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<{
    vaultId: string;
    member: VaultMemberItem;
  } | null>(null);

  const handleVaultInfoClick = useCallback(
    (vaultId: string, vaultName: string) => {
      setVaultInfoModal({ vaultId, vaultName });
      dispatch(getVaultMembers(vaultId))
        .unwrap()
        .then((payload) => {
          setVaultInfoModal((prev) => (prev ? { ...prev, members: payload.items } : null));
        })
        .catch((err: { message?: string }) => {
          setVaultInfoModal((prev) =>
            prev ? { ...prev, error: err?.message ?? 'Üyeler yüklenemedi' } : null
          );
        });
    },
    [dispatch]
  );

  const roleLabel = (role: number) => {
    switch (role) {
      case 0:
        return t('pages.workspace.vaultInfoRoleAdmin', 'Admin');
      case 1:
        return t('pages.workspace.vaultInfoRoleMember', 'Üye');
      default:
        return String(role);
    }
  };

  const handleRemoveVaultMember = useCallback(
    (vaultId: string, member: VaultMemberItem) => {
      const userName = member.userName ?? member.userEmail ?? member.userId;
      dispatch(removeVaultMemberByUserName({ vaultId, userName }))
        .unwrap()
        .then(() => {
          return dispatch(getVaultMembers(vaultId)).unwrap();
        })
        .then((payload) => {
          setVaultInfoModal((prev) => (prev ? { ...prev, members: payload.items } : null));
          refreshWorkspaceData();
        })
        .catch((err: { message?: string }) => {
          setVaultInfoModal((prev) =>
            prev ? { ...prev, error: err?.message ?? 'Üye kaldırılamadı' } : null
          );
        });
    },
    [dispatch, refreshWorkspaceData]
  );

  const handleConfirmRemoveMember = useCallback(() => {
    if (!removeMemberTarget) return;
    handleRemoveVaultMember(removeMemberTarget.vaultId, removeMemberTarget.member);
    setRemoveMemberTarget(null);
  }, [removeMemberTarget, handleRemoveVaultMember]);

  const handleFolderClick = (folderId: string) => {
    const next = selectedFolderId === folderId ? null : folderId;
    setSelectedFolderId(next);
    if (userId && next) {
      try {
        localStorage.setItem(`${WORKSPACE_LAST_FOLDER_KEY_PREFIX}${userId}`, next);
      } catch {
        // ignore
      }
    }
  };

  const handleTableOpenClick = (e: React.MouseEvent, table: TableItem) => {
    e.preventDefault();
    e.stopPropagation();
    const info: SelectedTableInfo = {
      tableId: table.id,
      tableName: table.name,
      folderId: table.folderId,
      folderName: selectedFolder?.name ?? '',
    };
    setTablesToOpenOnConfirm([info]);
    setVaultPasswords({});
    setIsVaultPasswordModalOpen(true);
  };

  const [isVaultPasswordModalOpen, setIsVaultPasswordModalOpen] = useState(false);
  /** Seçilen her vault için ayrı şifre: tableId -> vaultMasterPassword */
  const [vaultPasswords, setVaultPasswords] = useState<Record<string, string>>({});
  /** Tablolar açılırken kullanılacak liste (Aç butonu veya tek tıklama ile set edilir). */
  const [tablesToOpenOnConfirm, setTablesToOpenOnConfirm] = useState<SelectedTableInfo[] | null>(null);
  const [vaultPasswordError, setVaultPasswordError] = useState<string | null>(null);
  const [isCheckingVaultPassword, setIsCheckingVaultPassword] = useState(false);

  useEffect(() => {
    if (isVaultPasswordModalOpen) setVaultPasswordError(null);
  }, [isVaultPasswordModalOpen]);

  const setVaultPasswordForTable = useCallback((tableId: string, password: string) => {
    setVaultPasswords((prev) => ({ ...prev, [tableId]: password }));
    setVaultPasswordError(null);
  }, []);

  const handleVaultPasswordConfirm = async () => {
    const tables = tablesToOpenOnConfirm ?? [];
    if (tables.length === 0) return;
    const missing = tables.filter((t) => !(vaultPasswords[t.tableId] ?? '').trim());
    if (missing.length > 0) {
      setVaultPasswordError(t('pages.workspace.vaultPasswordRequiredForAll'));
      return;
    }
    setVaultPasswordError(null);
    const passwordsMap: Record<string, string> = {};
    tables.forEach((t) => {
      passwordsMap[t.tableId] = (vaultPasswords[t.tableId] ?? '').trim();
    });

    setIsCheckingVaultPassword(true);
    try {
      const results = await Promise.all(
        tables.map((t) =>
          dispatch(
            checkIfUserKnowsPassword({
              vaultId: t.tableId,
              vaultMasterPassword: passwordsMap[t.tableId],
            })
          ).unwrap()
        )
      );
      const allValid = results.every((v) => v === true);
      if (!allValid) {
        setVaultPasswordError(t('pages.workspace.vaultPasswordWrong', 'Şifre hatalı. Lütfen tekrar deneyin.'));
        return;
      }
      setIsVaultPasswordModalOpen(false);
      setTablesToOpenOnConfirm(null);
      setVaultPasswords({});
      navigate('/passwords', {
        state: { selectedTables: tables, vaultPasswords: passwordsMap },
      });
    } catch {
      setVaultPasswordError(t('pages.workspace.vaultPasswordWrong', 'Şifre hatalı. Lütfen tekrar deneyin.'));
    } finally {
      setIsCheckingVaultPassword(false);
    }
  };

  const handleVaultPasswordModalClose = () => {
    setIsVaultPasswordModalOpen(false);
    setTablesToOpenOnConfirm(null);
    setVaultPasswordError(null);
  };

  const openNameModal = useCallback((mode: NameModalMode, payload?: { folderId?: string; tableId?: string; currentName?: string }) => {
    setNameModal({ mode, ...payload });
  }, []);
  const closeNameModal = useCallback(() => setNameModal(null), []);

  const handleNameModalSave = useCallback(
    (name: string) => {
      if (!nameModal) return;
      switch (nameModal.mode) {
        case 'addFolder': {
          dispatch(createVaultFolder({ name })).then((result) => {
            if (createVaultFolder.fulfilled.match(result) && result.payload?.id) {
              setSelectedFolderId(result.payload.id);
              if (userId) {
                try {
                  localStorage.setItem(`${WORKSPACE_LAST_FOLDER_KEY_PREFIX}${userId}`, result.payload.id);
                } catch {
                  // ignore
                }
              }
              refreshWorkspaceData();
            }
          });
          break;
        }
        case 'renameFolder': {
          if (nameModal.folderId) {
            dispatch(updateVaultFolder({ id: nameModal.folderId, body: { name } })).then(() => {
              refreshWorkspaceData();
            });
          }
          break;
        }
        default:
          break;
      }
      closeNameModal();
    },
    [nameModal, closeNameModal, dispatch, userId, refreshWorkspaceData]
  );

  const handleRenameWithPasswordSave = useCallback(
    (name: string, vaultMasterPassword: string) => {
      if (!nameModal || nameModal.mode !== 'renameTable' || !nameModal.tableId || !userId) return;
      dispatch(updateVault({ id: nameModal.tableId, ownerId: userId, body: { name, vaultMasterPassword } })).then(() => {
        refreshWorkspaceData();
      });
      closeNameModal();
    },
    [nameModal, closeNameModal, dispatch, userId, refreshWorkspaceData]
  );

  const handleAddTableSave = useCallback(
    (name: string, vaultMasterPassword: string) => {
      if (!nameModal || nameModal.mode !== 'addTable' || !nameModal.folderId) return;
      dispatch(
        createVault({ name, folderId: nameModal.folderId, vaultMasterPassword })
      ).then(() => {
        refreshWorkspaceData();
      });
      closeNameModal();
    },
    [nameModal, closeNameModal, dispatch, refreshWorkspaceData]
  );

  const handleConfirmDelete = useCallback(
    (password?: string) => {
      if (!deleteTarget) return;
      if (deleteTarget.type === 'folder') {
        if (!userId) return;
        dispatch(deleteVaultFolder({ id: deleteTarget.id, ownerId: userId })).then(() => {
          refreshWorkspaceData();
        });
        if (selectedFolderId === deleteTarget.id) setSelectedFolderId(null);
      } else {
        if (!userId) return;
        const body = password != null && password.trim() !== '' ? { vaultMasterPassword: password } : undefined;
        dispatch(deleteVault({ id: deleteTarget.id, ownerId: userId, body })).then(() => {
          refreshWorkspaceData();
        });
      }
      setDeleteTarget(null);
    },
    [deleteTarget, selectedFolderId, userId, dispatch, refreshWorkspaceData]
  );

  const nameModalTitle =
    nameModal?.mode === 'addFolder'
      ? t('pages.workspace.addFolder')
      : nameModal?.mode === 'renameFolder'
        ? t('pages.workspace.renameFolder')
        : nameModal?.mode === 'addTable'
          ? t('pages.workspace.addTable')
          : t('pages.workspace.renameTable');
  const nameModalInitial =
    nameModal?.mode === 'renameFolder' || nameModal?.mode === 'renameTable' ? nameModal.currentName ?? '' : '';

  return (
    <div className="min-h-[calc(100vh-6rem)] lg:min-h-[calc(100vh-8rem)]">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 2xl:gap-8">
        {/* Klasörler - Sidebar card */}
        <aside className="lg:w-72 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800/80 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-lg shadow-gray-200/50 dark:shadow-none overflow-hidden">
            <div className="px-3 lg:px-4 2xl:px-5 py-3 2xl:py-4 border-b border-gray-100 dark:border-gray-700/80 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('pages.workspace.folders')}
              </h3>
              <button
                type="button"
                onClick={() => openNameModal('addFolder')}
                className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-primary/10 dark:hover:bg-primary/20 transition-all"
                title={t('pages.workspace.addFolder')}
              >
                <i className="fa-solid fa-plus text-sm" />
              </button>
            </div>
            <ul className="p-3 space-y-1.5">
              {vaultFolderListLoading ? (
                <li className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                  <i className="fa-solid fa-spinner fa-spin text-xl mr-2" aria-hidden />
                  <span className="text-sm">{t('common.loading', 'Yükleniyor...')}</span>
                </li>
              ) : folders.length === 0 ? (
                <li className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                  {t('pages.workspace.noFolders', 'Henüz klasör yok. Yeni klasör ekleyebilirsiniz.')}
                </li>
              ) : (
                folders.map((folder) => {
                  const isSelected = selectedFolderId === folder.id;
                  const isSharedFolder = folder.id === SHARED_FOLDER_ID;
                  return (
                    <li key={folder.id}>
                      <div
                        className={`
                          group flex items-center gap-2 rounded-xl transition-all duration-200
                          ${isSelected
                            ? 'bg-primary text-white shadow-md shadow-primary/30'
                            : 'bg-gray-50 dark:bg-gray-700/40 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                          }
                        `}
                      >
                        <button
                          type="button"
                          onClick={() => handleFolderClick(folder.id)}
                          className="flex-1 flex items-center justify-between gap-2 px-4 py-3 text-left min-w-0 rounded-xl"
                        >
                          <span className="flex items-center gap-3 min-w-0">
                            <span className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${isSelected ? 'bg-white/20' : 'bg-gray-200/80 dark:bg-gray-600/50'}`}>
                              <i className={`fa-solid ${isSharedFolder ? 'fa-share-nodes' : 'fa-folder'} text-base`} />
                            </span>
                            <span className="font-medium truncate">{folder.name}</span>
                          </span>
                          <i className={`fa-solid fa-chevron-down text-xs flex-shrink-0 transition-transform duration-200 ${isSelected ? 'rotate-180' : ''}`} />
                        </button>
                        {!isSharedFolder && (
                          <div
                            className={`flex items-center gap-0.5 flex-shrink-0 pr-2 ${isSelected ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => openNameModal('renameFolder', { folderId: folder.id, currentName: folder.name })}
                              className="p-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                              title={t('pages.workspace.renameFolder')}
                            >
                              <i className="fa-solid fa-pen text-xs" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget({ type: 'folder', id: folder.id, name: folder.name })}
                              className="p-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                              title={t('pages.workspace.deleteFolder')}
                            >
                              <i className="fa-solid fa-trash text-xs" />
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </aside>

        {/* Tablolar - Main content */}
        <main className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-800/80 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-lg shadow-gray-200/50 dark:shadow-none overflow-hidden min-h-[320px]">
            <div className="px-3 lg:px-5 2xl:px-6 py-3 2xl:py-4 border-b border-gray-100 dark:border-gray-700/80 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {selectedFolder
                  ? t('pages.workspace.tablesInFolder', { folder: selectedFolder.name })
                  : t('pages.workspace.selectFolder')}
              </h3>
              {folders.length > 0 && selectedFolderId && !isSharedFolderSelected && (
                <button
                  type="button"
                  onClick={() => openNameModal('addTable', { folderId: selectedFolderId })}
                  className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-plus" />
                  {t('pages.workspace.addTable')}
                </button>
              )}
            </div>

            <div className="p-3 lg:p-4 2xl:p-6">
              {selectedFolderId ? (
                tablesInFolder.length > 0 ? (
                  <>
                    <ul className="grid gap-3 2xl:gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {tablesInFolder.map((table) => {
                        const isOwnVault = table.folderId !== SHARED_FOLDER_ID;
                        return (
                          <li key={table.id}>
                            <div
                              className="group flex flex-col gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-600/80 bg-gray-50/50 dark:bg-gray-700/30 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-md text-left transition-all duration-200 cursor-pointer text-gray-800 dark:text-gray-200"
                              onClick={(e) => handleTableOpenClick(e, table)}
                            >
                              <div className="flex items-center gap-4 min-w-0">
                                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-white dark:bg-gray-600/50 text-gray-500 dark:text-gray-400">
                                  <i className="fa-solid fa-table-cells text-lg" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="font-semibold block truncate">{table.name}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 block">
                                    {t('pages.workspace.clickToOpen', 'Tıklayarak aç')}
                                  </span>
                                </div>
                              </div>
                              {isOwnVault && (
                                <div
                                  className="flex flex-wrap items-center justify-between gap-y-2 gap-x-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex flex-wrap items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => handleVaultInfoClick(table.id, table.name)}
                                      className="px-1 py-0.5 rounded-sm text-[10px] font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-primary/20 dark:hover:bg-primary/30 hover:text-primary transition-colors flex items-center gap-0.5"
                                    >
                                      <i className="fa-solid fa-circle-info text-[8px]" />
                                      <span>{t('pages.workspace.vaultInfo', 'Üyeler')}</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShareVaultTarget({ vaultId: table.id, vaultName: table.name })
                                      }
                                      className="px-1 py-0.5 rounded-sm text-[10px] font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-primary/20 dark:hover:bg-primary/30 hover:text-primary transition-colors flex items-center gap-0.5"
                                    >
                                      <i className="fa-solid fa-share-nodes text-[8px]" />
                                      <span>{t('pages.workspace.share')}</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openNameModal('renameTable', {
                                          tableId: table.id,
                                          currentName: table.name,
                                        })
                                      }
                                      className="px-1 py-0.5 rounded-sm text-[10px] font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-primary/20 dark:hover:bg-primary/30 hover:text-primary transition-colors flex items-center gap-0.5"
                                    >
                                      <i className="fa-solid fa-pen text-[8px]" />
                                      <span>{t('pages.workspace.renameTable')}</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setChangePasswordVaultTarget({ vaultId: table.id, vaultName: table.name })
                                      }
                                      className="px-1 py-0.5 rounded-sm text-[10px] font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-primary/20 dark:hover:bg-primary/30 hover:text-primary transition-colors flex items-center gap-0.5"
                                    >
                                      <i className="fa-solid fa-key text-[8px]" />
                                      <span>{t('pages.workspace.changeVaultMasterPassword')}</span>
                                    </button>
                                  </div>
                                  <div className="flex items-center border-l border-gray-300 dark:border-gray-500 pl-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setDeleteTarget({ type: 'table', id: table.id, name: table.name })
                                      }
                                      className="px-1 py-0.5 rounded-sm text-[10px] font-medium bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors flex items-center gap-0.5"
                                    >
                                      <i className="fa-solid fa-trash text-[8px]" />
                                      <span>{t('pages.workspace.deleteTable')}</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-20 h-20 rounded-2xl bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center mb-5">
                      <i className="fa-solid fa-table-cells text-4xl text-gray-400 dark:text-gray-500" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 font-medium mb-1">{t('pages.workspace.noTables')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">{t('pages.workspace.noTablesDescription')}</p>
                    {folders.length > 0 && !isSharedFolderSelected && (
                      <button
                        type="button"
                        onClick={() => openNameModal('addTable', { folderId: selectedFolderId! })}
                        className="px-6 py-3 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity font-medium flex items-center gap-2 shadow-lg shadow-primary/25"
                      >
                        <i className="fa-solid fa-plus" />
                        {t('pages.workspace.addTable')}
                      </button>
                    )}
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700/50 dark:to-gray-600/50 flex items-center justify-center mb-6">
                    <i className="fa-solid fa-folder-open text-5xl text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 font-medium mb-1">{t('pages.workspace.selectFolderHint')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 max-w-xs">{t('pages.workspace.selectFolderDescription')}</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {nameModal?.mode === 'addTable' ? (
        <AddTableModal
          isOpen={true}
          onClose={closeNameModal}
          onSave={handleAddTableSave}
          title={t('pages.workspace.addTable')}
          namePlaceholder={t('pages.workspace.namePlaceholder')}
          passwordLabel={t('pages.workspace.addTablePasswordLabel')}
          passwordPlaceholder={t('pages.workspace.addTablePasswordPlaceholder')}
          cancelText={t('common.cancel')}
          saveText={t('common.save')}
        />
      ) : nameModal?.mode === 'renameTable' ? (
        <RenameWithPasswordModal
          isOpen={true}
          onClose={closeNameModal}
          onSave={handleRenameWithPasswordSave}
          title={nameModalTitle}
          initialName={nameModalInitial}
          namePlaceholder={t('pages.workspace.namePlaceholder')}
          passwordLabel={t('pages.workspace.renameDeletePasswordLabel')}
          passwordPlaceholder={t('pages.workspace.renameDeletePasswordPlaceholder')}
          cancelText={t('common.cancel')}
          saveText={t('common.save')}
        />
      ) : (
        <NameEditModal
          isOpen={!!nameModal}
          onClose={closeNameModal}
          onSave={handleNameModalSave}
          title={nameModalTitle}
          initialValue={nameModalInitial}
          placeholder={nameModal?.mode === 'addFolder' ? t('pages.workspace.namePlaceholder') : undefined}
          cancelText={t('common.cancel')}
          saveText={t('common.save')}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget?.type === 'folder' ? t('pages.workspace.deleteFolderConfirmTitle') : t('pages.workspace.deleteTableConfirmTitle')}
        message={
          deleteTarget?.type === 'folder'
            ? t('pages.workspace.deleteFolderConfirmMessage', { name: deleteTarget.name })
            : t('pages.workspace.deleteTableConfirmMessage', { name: deleteTarget?.name ?? '' })
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmButtonStyle="danger"
        requirePassword={deleteTarget?.type === 'table'}
        passwordLabel={t('pages.workspace.renameDeletePasswordLabel')}
        passwordPlaceholder={t('pages.workspace.renameDeletePasswordPlaceholder')}
      />

      <ConfirmationModal
        isOpen={!!removeMemberTarget}
        onClose={() => setRemoveMemberTarget(null)}
        onConfirm={handleConfirmRemoveMember}
        title={t('pages.workspace.removeMemberConfirmTitle', 'Üyeyi kaldır')}
        message={
          removeMemberTarget
            ? t('pages.workspace.removeMemberConfirmMessage', {
                name: removeMemberTarget.member.userName ?? removeMemberTarget.member.userEmail ?? removeMemberTarget.member.userId,
              })
            : ''
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmButtonStyle="danger"
      />

      <ShareVaultModal
        isOpen={!!shareVaultTarget}
        onClose={() => setShareVaultTarget(null)}
        vaultId={shareVaultTarget?.vaultId ?? ''}
        vaultName={shareVaultTarget?.vaultName ?? ''}
        onSuccess={refreshWorkspaceData}
      />

      <ChangeVaultMasterPasswordModal
        isOpen={!!changePasswordVaultTarget}
        onClose={() => setChangePasswordVaultTarget(null)}
        vaultId={changePasswordVaultTarget?.vaultId ?? ''}
        vaultName={changePasswordVaultTarget?.vaultName ?? ''}
      />

      {vaultInfoModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
            onClick={() => setVaultInfoModal(null)}
            aria-hidden
          />
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/80 flex-shrink-0 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('pages.workspace.vaultInfoModalTitle', { name: vaultInfoModal.vaultName })}
                </h3>
                <button
                  type="button"
                  onClick={() => setVaultInfoModal(null)}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label={t('common.close', 'Kapat')}
                >
                  <i className="fa-solid fa-times" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                {vaultInfoModal.error ? (
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2" role="alert">
                    <i className="fa-solid fa-circle-exclamation flex-shrink-0" />
                    {vaultInfoModal.error}
                  </p>
                ) : vaultInfoModal.members === undefined ? (
                  <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                    <i className="fa-solid fa-spinner fa-spin text-2xl mr-2" aria-hidden />
                    <span className="text-sm">{t('common.loading', 'Yükleniyor...')}</span>
                  </div>
                ) : vaultInfoModal.members.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('pages.workspace.vaultInfoNoMembers', 'Bu vault\'ta henüz üye yok.')}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {vaultInfoModal.members
                      .filter((member) => member.role !== 2)
                      .map((member) => (
                      <li
                        key={`${member.vaultId}-${member.userId}`}
                        className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 border-b border-gray-100 dark:border-gray-700/80 last:border-0"
                      >
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-gray-900 dark:text-gray-100 block truncate">
                            {member.userName || member.userEmail || member.userId}
                          </span>
                          {member.userEmail && member.userName && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 block truncate">
                              {member.userEmail}
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {roleLabel(member.role)}
                        </span>
                        {member.addedAt && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(member.addedAt).toLocaleDateString()}
                          </span>
                        )}
                        {member.role === 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              vaultInfoModal.vaultId && setRemoveMemberTarget({ vaultId: vaultInfoModal.vaultId, member })
                            }
                            className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 rounded-lg transition-colors flex-shrink-0"
                            title={t('pages.workspace.removeMember', 'Üyeyi kaldır')}
                            aria-label={t('pages.workspace.removeMember', 'Üyeyi kaldır')}
                          >
                            <i className="fa-solid fa-times text-sm" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {isVaultPasswordModalOpen &&
        (() => {
          const tables = tablesToOpenOnConfirm ?? [];
          return createPortal(
            <>
              <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
                onClick={handleVaultPasswordModalClose}
              />
              <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/80 flex-shrink-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t('pages.workspace.vaultPasswordModalTitle')}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('pages.workspace.vaultPasswordModalMessagePerVault')}
                    </p>
                  </div>
                  <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
                    {tables.map((table, index) => (
                      <div key={table.tableId} className="space-y-1.5">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {table.tableName}
                        </label>
                        <input
                          type="text"
                          value={vaultPasswords[table.tableId] ?? ''}
                          onChange={(e) => setVaultPasswordForTable(table.tableId, e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleVaultPasswordConfirm()}
                          placeholder={t('pages.workspace.vaultPasswordPlaceholder')}
                          autoComplete="off"
                          autoFocus={index === 0}
                          className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow disabled:opacity-70 input-mask-password"
                          aria-label={table.tableName}
                        />
                      </div>
                    ))}
                    {vaultPasswordError && (
                      <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2" role="alert">
                        <i className="fa-solid fa-circle-exclamation flex-shrink-0" />
                        {vaultPasswordError}
                      </p>
                    )}
                  </div>
                  <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700/80 flex justify-end gap-3 flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleVaultPasswordModalClose}
                      className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors font-medium"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleVaultPasswordConfirm()}
                      disabled={isCheckingVaultPassword}
                      className="px-5 py-2.5 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity font-medium shadow-lg shadow-primary/25 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isCheckingVaultPassword ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" />
                          {t('common.loading', 'Yükleniyor...')}
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-folder-open" />
                          {t('pages.workspace.open')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>,
            document.body
          );
        })()}
    </div>
  );
};


export default Workspace;
